/**
 * 拼音检测和输入法状态管理工具
 */

// 拼音声母和韵母的正则表达式
const PINYIN_REGEX = /^[a-z]+$/i;

// 常见的拼音声母
const INITIALS = [
  "b",
  "p",
  "m",
  "f",
  "d",
  "t",
  "n",
  "l",
  "g",
  "k",
  "h",
  "j",
  "q",
  "x",
  "zh",
  "ch",
  "sh",
  "r",
  "z",
  "c",
  "s",
  "y",
  "w",
];

// 常见的拼音韵母
const FINALS = [
  "a",
  "o",
  "e",
  "i",
  "u",
  "ü",
  "ai",
  "ei",
  "ui",
  "ao",
  "ou",
  "iu",
  "ie",
  "üe",
  "er",
  "an",
  "en",
  "in",
  "un",
  "ün",
  "ang",
  "eng",
  "ing",
  "ong",
];

/**
 * 检测输入是否为拼音
 * @param input 输入字符串
 * @returns 是否为拼音
 */
export function isPinyin(input: string): boolean {
  if (!input || typeof input !== "string") return false;

  const trimmed = input.trim();
  if (!trimmed) return false;

  // 分割空格，检查每个部分是否都是拼音
  const parts = trimmed.split(/\s+/);

  return parts.every((part) => {
    // 检查是否只包含字母
    if (!PINYIN_REGEX.test(part)) return false;

    // 检查长度（单个拼音不会太长）
    if (part.length > 6) return false;

    const lowerPart = part.toLowerCase();

    // 检查是否以声母开头
    const startsWithInitial = INITIALS.some((initial) =>
      lowerPart.startsWith(initial)
    );

    // 对于短的输入，只要以声母开头就认为是拼音
    if (part.length <= 2 && startsWithInitial) return true;

    // 检查是否包含韵母
    const containsFinal = FINALS.some((final) => lowerPart.includes(final));

    // 如果以声母开头且包含韵母，很可能是拼音
    if (startsWithInitial && containsFinal) return true;

    // 检查是否包含常见的拼音组合
    const commonPinyinPatterns = [
      /^[a-z]+[aeiouü][a-z]*$/i, // 包含元音
      /^[a-z]+(ng|n|r)$/i, // 以ng、n、r结尾
      /^[a-z]+(ai|ei|ui|ao|ou|iu|ie|üe|er)$/i, // 复合韵母
    ];

    return commonPinyinPatterns.some((pattern) => pattern.test(lowerPart));
  });
}

/**
 * 检测输入是否包含中文字符
 * @param input 输入字符串
 * @returns 是否包含中文
 */
export function containsChinese(input: string): boolean {
  if (!input || typeof input !== "string") return false;
  return /[\u4e00-\u9fff]/.test(input);
}

/**
 * 检测输入是否为纯拼音（不包含中文）
 * @param input 输入字符串
 * @returns 是否为纯拼音
 */
export function isPurePinyin(input: string): boolean {
  if (!input || typeof input !== "string") return false;

  const trimmed = input.trim();
  if (!trimmed) return false;

  // 不包含中文字符
  if (containsChinese(trimmed)) return false;

  // 是拼音格式
  return isPinyin(trimmed);
}

/**
 * 检测输入是否可能是正在输入拼音（未完成）
 * @param input 输入字符串
 * @returns 是否可能是未完成的拼音
 */
export function isIncompletePinyin(input: string): boolean {
  if (!input || typeof input !== "string") return false;

  const trimmed = input.trim();
  if (!trimmed) return false;

  // 不包含中文字符
  if (containsChinese(trimmed)) return false;

  // 分割空格，检查是否有未完成的拼音部分
  const parts = trimmed.split(/\s+/);

  return parts.some((part) => {
    // 只包含字母
    if (!PINYIN_REGEX.test(part)) return false;

    // 长度很短，肯定是未完成的
    if (part.length <= 1) return true;

    const lowerPart = part.toLowerCase();

    // 检查是否以声母开头
    const startsWithInitial = INITIALS.some((initial) =>
      lowerPart.startsWith(initial)
    );

    if (!startsWithInitial) return false;

    // 对于长度 2-4 的输入，更严格地判断是否完整
    if (part.length <= 4) {
      // 常见的完整拼音模式
      const completePatterns = [
        /^(zh|ch|sh|[bpmfdtnlgkhjqxzcsryw])(a|o|e|ai|ei|ui|ao|ou|iu|ie|üe|er|an|en|in|un|ün|ang|eng|ing|ong)$/i,
        /^[aeiouü]$/i, // 单独的韵母
        /^(zh|ch|sh|[bpmfdtnlgkhjqxzcsryw])[aeiouü]$/i, // 声母+单韵母
      ];

      const isComplete = completePatterns.some((pattern) =>
        pattern.test(lowerPart)
      );

      // 如果不匹配完整模式，认为是未完成的
      if (!isComplete) return true;

      // 即使匹配完整模式，如果长度很短（<=2），也可能是未完成的
      if (part.length <= 2) return true;
    }

    return false;
  });
}

/**
 * 输入法状态管理类
 */
export class InputMethodManager {
  private static instance: InputMethodManager;
  private isComposing: boolean = false;

  static getInstance(): InputMethodManager {
    if (!InputMethodManager.instance) {
      InputMethodManager.instance = new InputMethodManager();
    }
    return InputMethodManager.instance;
  }

  /**
   * 开始输入法组合
   */
  startComposition(): void {
    this.isComposing = true;
  }

  /**
   * 结束输入法组合
   */
  endComposition(): void {
    this.isComposing = false;
  }

  /**
   * 检查是否应该触发搜索
   * @param input 输入内容
   * @returns 是否应该触发搜索
   */
  shouldTriggerSearch(input: string): boolean {
    // 如果正在输入法组合，绝对不触发搜索
    if (this.isComposing) return false;

    // 如果输入为空，不触发搜索
    if (!input || !input.trim()) return false;

    // 不在组合过程中，可以触发搜索
    return true;
  }

  /**
   * 重置状态
   */
  reset(): void {
    this.isComposing = false;
  }

  /**
   * 获取当前输入法状态（用于调试）
   */
  getDebugInfo(): { isComposing: boolean } {
    return {
      isComposing: this.isComposing,
    };
  }
}

// 导出单例实例
export const inputMethodManager = InputMethodManager.getInstance();

// 在开发环境下导入测试
// Tests removed to fix build
// if (import.meta.env.DEV) {
//   import('./pinyin.test')
//   import('./search-optimization.test')
// }
